import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ICON_GROUPS, iconComponent, iconLabel } from './linkMeta';
import { cn } from '../../lib/utils';

/**
 * LinkIconPicker — the icon chooser for a project link.
 *
 * A grid of lucide glyphs rather than a favicon fetch. Favicons would mean the
 * bar makes a request per link to a third-party host on every render of the
 * console, and links to private consoles frequently return nothing at all.
 * A chosen glyph always renders, offline included.
 *
 * Most users should never open this: the editor guesses from the hostname on
 * paste, and this is the override.
 */
export function LinkIconPicker({ value, onChange, disabled }) {
    const [open, setOpen] = useState(false);
    const Current = iconComponent(value);

    if (disabled) {
        return (
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-black/20">
                <Current className="h-4 w-4 text-muted-foreground" />
            </div>
        );
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    title={`Icon: ${iconLabel(value)} — click to change`}
                    aria-label="Change link icon"
                    data-testid="link-icon-picker"
                    className={cn(
                        'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border transition-colors',
                        open
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-white/10 bg-black/20 text-muted-foreground hover:border-white/20 hover:text-foreground'
                    )}
                >
                    <Current className="h-4 w-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                sideOffset={6}
                className="max-h-80 w-72 overflow-y-auto rounded-2xl border-white/10 bg-[hsl(240_11%_7%)]/95 p-3 backdrop-blur-xl"
            >
                {ICON_GROUPS.map((group) => (
                    <div key={group.label} className="mb-3 last:mb-0">
                        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                            {group.label}
                        </p>
                        <div className="grid grid-cols-8 gap-1">
                            {Object.entries(group.icons).map(([key, meta]) => {
                                const Icon = meta.icon;
                                const active = key === value;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        title={meta.label}
                                        aria-label={meta.label}
                                        aria-pressed={active}
                                        onClick={() => {
                                            onChange(key);
                                            setOpen(false);
                                        }}
                                        className={cn(
                                            'flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
                                            active
                                                ? 'bg-primary text-primary-foreground'
                                                : 'text-muted-foreground hover:bg-white/10 hover:text-foreground'
                                        )}
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </PopoverContent>
        </Popover>
    );
}

export default LinkIconPicker;
